import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-hot-toast'
import flowValveApi from '@/api/config/flowValveApi'
import { queryKeys } from '@/constant/common'
import { getApiErrorMessage } from '@/helpers'

// Same wait as the acknowledge-all hook: the reset reaches SRC immediately,
// but the pipe row reads its state from FlowRule.State in the CisgenX database
// — which only changes once the sync has pulled the cleared state back.
const RESET_PROPAGATION_DELAY_MS = 800

const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms))

interface AcknowledgePipeErrorsArgs {
  interfaceId: number
  /** Every flow valve on the pipe's rule. */
  valveIds: number[]
}

/**
 * Clears the errors on ONE pipe.
 *
 * There is no per-pipe or per-rule reset in the API. `reset-error-by-id` takes
 * a flow VALVE, and resolves the parent rule itself — it calls SRC's
 * `flowValve/<id>/resetError` and `flowRule/<id>/resetError` together
 * (FlowValveService.ResetErrorByIdAsync). Clearing the rule is what clears the
 * pipe's status.
 *
 * Every valve on the rule is reset, not just one: the call clears the rule on
 * the controller but only the named valve's error columns in the CisgenX
 * database, so stopping after the first would leave its siblings showing
 * errors that are no longer real.
 */
const acknowledgePipeErrors = async ({
  interfaceId,
  valveIds,
}: AcknowledgePipeErrorsArgs) => {
  // Serial, matching the rest of this API's write endpoints, which return 500
  // when writes for one interface arrive together.
  for (const id of valveIds) {
    await flowValveApi.resetErrorById(interfaceId, id)
  }
}

const useAcknowledgePipeErrors = () => {
  const queryClient = useQueryClient()

  return useMutation(acknowledgePipeErrors, {
    onSuccess: async () => {
      await wait(RESET_PROPAGATION_DELAY_MS)
      await Promise.all([
        queryClient.refetchQueries([queryKeys.flowValve]),
        queryClient.refetchQueries([queryKeys.flowValveError]),
        queryClient.refetchQueries([queryKeys.hydraulic]),
        queryClient.refetchQueries([queryKeys.flowRule]),
      ])
    },
    onError: (error: any) => {
      toast.error(getApiErrorMessage(error))
    },
  })
}

export default useAcknowledgePipeErrors
